//src/factory/symbols/createSocketSymbols.js
import * as THREE from 'three';
import { generateUniqueId } from '../../utils/generateUniqueId';

export function createSingleSocket(
  scene,
  mousePos,
  dir = 1,
  color = 0x0045ff,
  isPreview = false,
  type = "kapaklı",
  tempLineUUID,
  rotateValue,
  providedSocketId, // optional: if provided, use this id instead of generating a new one
  stretchLength = 0 // optional: stretch amount applied to line1 and relative positions
) {


  const group = new THREE.Group();
  const offsetGroup = new THREE.Group();
  // offsetGroup'u tanımlamak için userData ekle
  offsetGroup.userData = { isOffsetGroup: true, stretchLength: stretchLength };
  dir = dir ?? 1;
  const width = 14;
  const height = 28;
  const rotationGizmoColor = isPreview ? 0x3333ff : 0x3333ff;
  const strechGizmoColor = isPreview ?  0x33ff33 :  0x33ff33;
  const symetricGizmoColor = isPreview ? 0xff3aa3 : 0xff3aa3;
  const moveGizmoColor = isPreview ? 0xff3223 : 0x00ffff;
  const innerRadius = 4; // Gizmo boyutunu büyüttük (2'den 4'e)
  const basePoint = { x: mousePos.x, y: mousePos.y };
  const socketId = providedSocketId || generateUniqueId('socket');
  // rotateValue'yu normalize et (undefined veya null ise 0)
  const normalizedRotate = typeof rotateValue === 'number' ? rotateValue : 0;
  // stretchLength'i normalize et
  const normalizedStretch = typeof stretchLength === 'number' ? stretchLength : 0;

  group.userData = {
    id: socketId,
    type: 'socket',
    subtype: type, // ✅ Store the specific socket type (normal, kapaklı, etanj)
    isSelectable: true,
    isPreview: isPreview,
    connectedTo: null,
    basePoint,
    tempLineUUID,
    power: 300,
    rotate: normalizedRotate, // Normalize edilmiş rotate değeri (degrees)
    stretchLength: normalizedStretch,
    entries: [
      {
        id: generateUniqueId('entry'),
        type: 'breaker',
        label: 'Priz Sigortası',
        MCB_No: 433, // 16A, 1P, B curve (priz hatları için)
        phases: 1
      }
    ],
    outs: []
  }; 
  group.position.set(basePoint.x, basePoint.y, 0);
  group.add(offsetGroup);
  const lineMaterial = new THREE.LineBasicMaterial({ color: color });

  // ✅ line1: base length 21 + stretchLength
  const line1Length = 21 + normalizedStretch;
  const line1Geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, line1Length, 0),
  ]);
  const line1 = new THREE.Line(line1Geo, lineMaterial);
  line1.userData = { originalColor: lineMaterial.color.getHex(), role: 'line1', socketId };
  offsetGroup.add(line1);

  // ✅ line2: y position adjusted by stretchLength
  const line2Geo = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(-7, 21 + normalizedStretch, 0),
    new THREE.Vector3(7, 21 + normalizedStretch, 0),
  ]);
  const line2 = new THREE.Line(line2Geo, lineMaterial);
  line2.userData = { originalColor: lineMaterial.color.getHex(), role: 'line2', socketId };
  offsetGroup.add(line2);



  // ✅ line3 (kapaklı): positions adjusted by stretchLength
  if (type === 'kapaklı') {
    const line3Geo = new THREE.BufferGeometry().setFromPoints([
      new THREE.Vector3(0, 21 + normalizedStretch, 0),
      new THREE.Vector3(0, 28 + normalizedStretch, 0),
      new THREE.Vector3(7, 28 + normalizedStretch, 0),
    ]);
    const line3 = new THREE.Line(line3Geo, lineMaterial);
    line3.userData = { originalColor: lineMaterial.color.getHex(), role: 'line3', socketId };
    offsetGroup.add(line3);
  }

  // ✅ etanjFill: position adjusted by stretchLength
  if (type === 'etanj') {
    const etanjShape = new THREE.Shape();
    etanjShape.moveTo(0, 0);
    etanjShape.absarc(0, 0, 7, 0, Math.PI / 2, false);
    etanjShape.lineTo(0, 0);

    const geometry = new THREE.ShapeGeometry(etanjShape);
    const material = new THREE.MeshBasicMaterial({ color: lineMaterial.color.getHex(), side: THREE.DoubleSide });
    const etanjFill = new THREE.Mesh(geometry, material);
    etanjFill.position.set(0, 28 + normalizedStretch, 0);
    etanjFill.rotation.z = Math.PI;
    etanjFill.userData = { role: 'etanjFill', socketId };
    offsetGroup.add(etanjFill);
  }


  const createFilledTriangle = (p1, p2, p3, color) => {
    const shape = new THREE.Shape();
    shape.moveTo(p1.x, p1.y);
    shape.lineTo(p2.x, p2.y);
    shape.lineTo(p3.x, p3.y);
    shape.lineTo(p1.x, p1.y); // üçgeni kapat

    const geometry = new THREE.ShapeGeometry(shape);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };
  const createFilledTetragon = (p1, p2, p3, p4, color) => {
    const shape = new THREE.Shape();
    shape.moveTo(p1.x, p1.y);
    shape.lineTo(p2.x, p2.y);
    shape.lineTo(p3.x, p3.y);
    shape.lineTo(p4.x, p4.y);
    shape.lineTo(p1.x, p1.y); // üçgeni kapat

    const geometry = new THREE.ShapeGeometry(shape);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };
  const createFilledCircle = (radius, color) => {
    const geometry = new THREE.CircleGeometry(radius, 64);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };


  // ✅ Stretch gizmo - positioned at line1 end (base y=21 + stretchLength)
  const strechGizmoP1 = new THREE.Vector3(10, 0, 0);
  const strechGizmoP2 = new THREE.Vector3(7, 3, 0);
  const strechGizmoP3 = new THREE.Vector3(4, 0, 0);
  const strechGizmo = createFilledTriangle(strechGizmoP1, strechGizmoP2, strechGizmoP3, strechGizmoColor);
  strechGizmo.position.set(0, normalizedStretch, 0);

  // ✅ Symmetric gizmo - positioned relative to stretch
  const symetricGizmoP1 = new THREE.Vector3(-10, 0, 0);
  const symetricGizmoP2 = new THREE.Vector3(-7, 3, 0);
  const symetricGizmoP3 = new THREE.Vector3(-4, 0, 0);
  const symetricGizmoP4 = new THREE.Vector3(-7, -3, 0);
  const symetrical = createFilledTetragon(symetricGizmoP1, symetricGizmoP2, symetricGizmoP3, symetricGizmoP4, symetricGizmoColor);
  symetrical.position.set(0, normalizedStretch, 0);

  // ⚠️ MOVE GIZMO ŞİMDİLİK İPTAL (geçici olarak devre dışı)
  /*
  const moveGizmoP1 = new THREE.Vector3(7, 7, 0);
  const moveGizmoP2 = new THREE.Vector3(7, 13, 0);
  const moveGizmoP3 = new THREE.Vector3(13, 13, 0);
  const moveGizmoP4 = new THREE.Vector3(13, 7, 0);

  const moveGizmo = createFilledTetragon(moveGizmoP1, moveGizmoP2, moveGizmoP3, moveGizmoP4, moveGizmoColor)
  */

  const innerCircle = createFilledCircle(innerRadius, rotationGizmoColor);
  innerCircle.userData = {
    type: 'rotateGizmo',
    parentSocketId: socketId,
  };
  symetrical.userData = {
    type: 'symetricGizmo',
    parentSocketId: socketId,
  };
  strechGizmo.userData = {
    type: 'stretchGizmo',
    parentSocketId: socketId,
  };
  /*
  moveGizmo.userData = {
    type: 'moveGizmo',
    parentSocketId: socketId,
  };
  */
  // ✅ Rotate gizmo - positioned at top of symbol (height + 5 + stretchLength)
  innerCircle.position.set(width / 2 - 7, height + 5 + normalizedStretch, 0.2);



  innerCircle.visible = false
  strechGizmo.visible = false
  symetrical.visible = false
  // moveGizmo.visible = false  // ⚠️ İPTAL


  offsetGroup.add(innerCircle);
  offsetGroup.add(strechGizmo);
  offsetGroup.add(symetrical);
  // offsetGroup.add(moveGizmo);  // ⚠️ İPTAL


  // ✅ Arc: center position adjusted by stretchLength (base y=28 + stretchLength)
  const arcRadius = 7;
  const arcCenter = new THREE.Vector3(0, 28 + normalizedStretch, 0);
  const arcPoints = [];
  for (let i = 0; i <= 16; i++) {
    const angle = -Math.PI * (i / 16);
    const x = arcCenter.x + arcRadius * Math.cos(angle);
    const y = arcCenter.y + arcRadius * Math.sin(angle);
    arcPoints.push(new THREE.Vector3(x, y, 0));
  }
  const arcGeometry = new THREE.BufferGeometry().setFromPoints(arcPoints);
  const arcLine = new THREE.Line(arcGeometry, lineMaterial);
  arcLine.userData = { originalColor: lineMaterial.color.getHex(), role: 'arc' };

  offsetGroup.add(arcLine);

  // 🔧 Offset uygulaması (merkez uca oturtulacak)
  // dir may be a numeric flip (-1 or 1) or a THREE.Vector3 direction.
  const yScale = (typeof dir === 'number') ? dir : 1;
  offsetGroup.scale.set(1, yScale, 1);
  offsetGroup.position.set(0, 0, 0);

  // If a direction vector is passed, use it for rotation (will be overridden externally if needed)
  if (dir instanceof THREE.Vector3) {
    const angle = Math.atan2(dir.y, dir.x); // Radians
    // Direction açısına ek olarak rotateValue'yu da ekle (kullanıcı 'r' tuşu ile rotasyon eklemişse)
    const rotateOffsetRadians = normalizedRotate * Math.PI / 180;
    offsetGroup.rotation.z = angle + rotateOffsetRadians;
  } else {
    // apply rotation (normalizedRotate is in degrees, convert to radians)
    offsetGroup.rotation.z = normalizedRotate * Math.PI / 180;
  }
 /*  if (scene && !isPreview) {
    scene.add(group);
  } */

  return group;
}

export function socketBuatControl(
  scene,
  sockets,
  color = 0x0045ff,
  generatedWireID,
  branchID
) {
  const createFilledCircle = (radius, color) => {
    const geometry = new THREE.CircleGeometry(radius, 64);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };

  sockets.forEach((socket, s) => {
    if (s < sockets.length - 1) {
      const buatCircle = createFilledCircle(2, color);
      buatCircle.userData = {
        type: 'buatCircle',
        socketUUID: socket.uuid,
        wireID: generatedWireID,
        branchID
      };
      buatCircle.position.set(socket.position.x, socket.position.y, 0.2);
      if (scene) {
        scene.add(buatCircle);
      }
    }
  });

}
export function singleCircleBuat(

  point, panelID,
  socketUUID,
  color = 0x0045ff,
) {
  const createFilledCircle = (radius, color) => {
    const geometry = new THREE.CircleGeometry(radius, 64);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    return new THREE.Mesh(geometry, material);
  };

  const buatCircle = createFilledCircle(2, color);
  buatCircle.userData = {
    socketUUID,
    type: 'buatCircle',
    panelID,
    isSelectable: true,
    isDraggable: true

  };
  buatCircle.position.set(point.x, point.y, 0.2); // Z pozisyonunu yükselttik
  return buatCircle

}

export function singleSquareBuat(
  scene,
  point,
  color = 0x0045ff,
  uuid
) {
  
  const square = new THREE.Mesh(
    new THREE.PlaneGeometry(5, 5),
    new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide })
  );
  square.position.set(point.x, point.y, 0.2);
  square.userData = {
    type: 'buatSquare',
    socketUUID: uuid,

  };
  scene.add(square);
  return square;


}